// Animation debug panel: fakes game events and weather locally (nothing is sent to the
// server) so every effect and announcement can be previewed on demand.
import { motion } from "framer-motion";

import * as uf from "../../../../common/unfathomable";
import { DragPanel } from "./DragPanel";
import type { MoodOverride } from "./three/Scene3D";
import css from "./Unfathomable.module.css";

export function DebugPanel({ view, emit, mood, setMood, onBolt, onClose, onPreviewEnd }: {
  view: uf.GameView;
  /** Inject fake events (local only). */
  emit: (events: uf.GameEvent[]) => void;
  mood: MoodOverride | null;
  setMood: (m: MoodOverride | null) => void;
  onBolt: () => void;
  onClose: () => void;
  /** Show the end screen with a made-up result. */
  onPreviewEnd: (humansWin: boolean) => void;
}) {
  const room = (): uf.InteriorSpaceId => uf.kDamageableSpaces[Math.floor(Math.random() * uf.kDamageableSpaces.length)];
  const me = view.you ?? view.current;
  const wp = Object.keys(uf.kWaypointById)[Math.floor(Math.random() * Object.keys(uf.kWaypointById).length)];
  const die = () => { const v = 1 + Math.floor(Math.random() * 8); return { type: "die", value: v, raw: v, reason: "调试掷骰" } as uf.GameEvent; };
  const mythosIds = Object.keys(uf.kMythosById);
  const someone = view.players[Math.floor(Math.random() * view.players.length)];
  const buttons: [string, () => void][] = [
    ["▶ 回合开始", () => emit([{ type: "turn", seat: someone.seat, turn: view.turnNumber }])],
    ["▶ 轮到我", () => emit([{ type: "turn", seat: me, turn: view.turnNumber }])],
    ["🜏 神话阶段", () => emit([{ type: "mythos", card: mythosIds[Math.floor(Math.random() * mythosIds.length)] }])],
    ["⚓ 全速前进", () => emit([{ type: "track", track: "travel", value: Math.min(4, view.travel + 1) }])],
    ["🧭 驶向新航点", () => emit([{ type: "waypoint", id: wp, by: me }])],
    ["✶ 仪式推进", () => emit([{ type: "track", track: "ritual", value: Math.min(4, view.ritual + 1) }])],
    ["✷ 大驱逐咒", () => emit([{ type: "banishment" }])],
    ["👁 觉醒", () => emit([{ type: "awakening" }])],
    ["💥 结构损伤", () => { const sp = room(); emit([{ type: "damageDrawn", card: "sdAny" }, { type: "damage", space: sp, card: "sdAny" }]); }],
    ["🛢 燃料泄漏卡", () => emit([{ type: "damageDrawn", card: "fuelLeak" }])],
    ["🌊 储藏被淹卡", () => emit([{ type: "damageDrawn", card: "floodedFoodStores" }])],
    ["🏃 乘客逃散卡", () => emit([{ type: "damageDrawn", card: "fleeingPassengers" }])],
    ["🐙 海德拉浮出", () => emit([{ type: "monster", what: "hydra", from: "deep", to: "W14", count: 1 }])],
    ["🔱 大衮浮出", () => emit([{ type: "monster", what: "dagon", from: "deep", to: "W58", count: 1 }])],
    ["🐟 深潜者浮出", () => emit([{ type: "monster", what: "deepOne", from: "deep", to: "Wu3", count: 2 }])],
    ["⛓ 关禁闭 → 船长易主", () => {
      const from = view.titles.captain ?? me;
      const next = view.players.find(p => p.seat !== from)?.seat ?? me;
      emit([{ type: "move", who: from, to: "brig" }, { type: "title", title: "captain", seat: next, from, why: "brig" }]);
    }],
    ["🩸 公开 → 典籍易手", () => {
      const from = view.titles.keeper ?? me;
      const next = view.players.find(p => p.seat !== from)?.seat ?? me;
      emit([{ type: "reveal", seat: from }, { type: "title", title: "keeper", seat: next, from, why: "reveal" }]);
    }],
    ["🐟 深潜者逐个激活", () => {
      const start = (Object.keys(view.spaces) as uf.SpaceId[]).find(sp => view.spaces[sp].deepOnes > 0 && uf.kSpaces[sp].arrow) ?? "W14";
      const evs: uf.GameEvent[] = [];
      let sp: uf.SpaceId = start;
      for (let step = 1; step <= 3; step++) {
        const to = uf.kSpaces[sp].arrow;
        if (!to) { evs.push({ type: "deepOneAct", space: sp, step, total: 3, act: "stay" }); continue; }
        evs.push({ type: "deepOneAct", space: sp, step, total: 3, act: "move", to }, { type: "monster", what: "deepOne", from: sp, to, count: 1 });
        sp = to;
      }
      emit(evs);
    }],
    ["🩸 叛徒现身", () => emit([{ type: "reveal", seat: me }])],
    ["☠ 角色倒下", () => emit([{ type: "defeat", target: "character", seat: me, space: view.players[me]?.space }])],
    ["🎲 掷骰", () => emit([die()])],
    ["✔ 检定通过", () => emit([{ type: "check", result: "pass", total: 9, title: "调试检定", target: 8 }])],
    ["✘ 检定失败", () => emit([{ type: "check", result: "fail", total: 3, title: "调试检定", target: 8 }])],
    ["📉 资源损失", () => emit([{ type: "resource", resource: "food", delta: -1 }, { type: "resource", resource: "sanity", delta: -2 }])],
    ["⚡ 闪电", onBolt],
  ];
  return (
    <motion.div className={css.debugPanelPos} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
    <DragPanel className={`${css.debugPanel} ${css.darkFrame}`}>
      <div className={css.debugHead}>
        <b>🛠 动画调试</b><span style={{ opacity: 0.7, fontSize: "0.75em" }}>（仅本地，不影响对局）</span>
        <button className={`${css.btn} ${css.btnSmall} ${css.btnGhost}`} style={{ marginLeft: "auto" }} onClick={onClose}>✕</button>
      </div>
      <div className={css.debugGrid}>
        {buttons.map(([label, fn]) => <button key={label} className={`${css.btn} ${css.btnSmall}`} onClick={fn}>{label}</button>)}
      </div>
      <div className={css.debugRow}>
        <span>风暴</span>
        <input type="range" min={0} max={1} step={0.05} value={mood?.storm ?? 0.2}
          onChange={e => setMood({ storm: Number(e.target.value), win: false, lose: false })} />
        <span>{(mood?.storm ?? 0).toFixed(2)}</span>
      </div>
      <div className={css.debugRow}>
        <button className={`${css.btn} ${css.btnSmall}`} onClick={() => setMood({ storm: 0, win: true, lose: false })}>☀ 胜利晴空</button>
        <button className={`${css.btn} ${css.btnSmall}`} onClick={() => setMood({ storm: 1, win: false, lose: true })}>🌩 失败风暴</button>
        <button className={`${css.btn} ${css.btnSmall}`} title="失败结局：爆炸、断裂、沉没（点「跟随对局」恢复船只）"
          onClick={() => { setMood(null); setTimeout(() => setMood({ storm: 1, win: false, lose: true, wreck: true }), 50); }}>💥 沉船</button>
        <button className={`${css.btn} ${css.btnSmall} ${css.btnGhost}`} onClick={() => setMood(null)}>↺ 跟随对局</button>
        <button className={`${css.btn} ${css.btnSmall}`} onClick={() => onPreviewEnd(true)}>🏁 胜利画面</button>
        <button className={`${css.btn} ${css.btnSmall}`} onClick={() => onPreviewEnd(false)}>🏁 失败画面</button>
      </div>
    </DragPanel>
    </motion.div>
  );
}
